/**
 * Centralized Company Information
 *
 * All company info should come from database via /api/v2/settings/company-info
 * COMPANY_DEFAULTS is kept for backwards compatibility but should be phased out.
 * Use getCompanyInfo() which fetches from database.
 */

// ============================================
// BACKWARDS COMPATIBILITY - Legacy imports use this
// These values should match what's in the database
// ============================================
export const COMPANY_DEFAULTS = {
  name: 'Smart Blinds™',  // Fallback — database value takes priority
  phone: '', // Must be configured in database
  phoneRaw: '',
  phoneTel: '',
  phoneInternational: '',

  email: {
    sales: '',
    support: '',
    warranty: '',
    notifications: '',
  },

  address: {
    street: '',
    city: '',
    state: '',
    zip: '',
    full: '',
  },

  tagline: '',
  description: '',

  hours: {
    weekday: '',
    saturday: '',
    sunday: '',
    full: '',
  },

  social: {
    twitter: '',
  },

  siteUrl: process.env.NEXT_PUBLIC_SITE_URL || 'http://localhost:3000',
} as const;

// ============================================
// TYPE DEFINITIONS
// ============================================
export interface CompanyInfo {
  companyName: string;
  emergencyHotline: string;
  tagline: string;
  salesEmail?: string;
  supportEmail?: string;
}

// ============================================
// CACHED API FETCH (must come from database)
// ============================================
let cachedCompanyInfo: CompanyInfo | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Get company info with caching
 * Fetches from API - data must be configured in database
 *
 * @param forceRefresh - Bypass cache and fetch fresh data
 */
export async function getCompanyInfo(forceRefresh = false): Promise<CompanyInfo> {
  const now = Date.now();

  // Return cached data if valid
  if (!forceRefresh && cachedCompanyInfo && (now - cacheTimestamp) < CACHE_DURATION) {
    return cachedCompanyInfo;
  }

  try {
    // Determine base URL for server-side vs client-side
    const baseUrl = typeof window === 'undefined'
      ? process.env.NEXT_PUBLIC_SITE_URL || 'http://localhost:3000'
      : '';

    const response = await fetch(`${baseUrl}/api/v2/settings/company-info`, {
      next: { revalidate: 300 }, // Next.js cache for 5 minutes
      cache: 'force-cache',
    });

    if (response.ok) {
      const result = await response.json();
      if (result.success && result.data?.companyInfo) {
        cachedCompanyInfo = {
          companyName: result.data.companyInfo.companyName,
          emergencyHotline: result.data.companyInfo.emergencyHotline,
          tagline: result.data.companyInfo.tagline,
          salesEmail: result.data.companyInfo.salesEmail,
          supportEmail: result.data.companyInfo.supportEmail,
        };
        cacheTimestamp = now;
        return cachedCompanyInfo;
      }
    }

    console.error('Company info not configured in database');
    throw new Error('Company info must be configured in database');
  } catch (error) {
    console.error('Failed to fetch company info:', error);
    throw error;
  }
}

/**
 * Get company info synchronously (uses cached value only)
 * Use this when you can't await (e.g., in static metadata)
 * Returns null if not cached - caller must handle this
 */
export function getCompanyInfoSync(): CompanyInfo | null {
  return cachedCompanyInfo;
}

/**
 * Helper to format phone for tel: links
 */
export function formatPhoneForTel(phone: string): string {
  return `tel:+1${phone.replace(/[^0-9]/g, '')}`;
}

/**
 * Clear the cache (useful for admin updates)
 */
export function clearCompanyInfoCache(): void {
  cachedCompanyInfo = null;
  cacheTimestamp = 0;
}
